import type {
    ObjectPageResponsiveTableWithMultiSelect,
    ObjectPageResponsiveTableWithInlineDelete,
    ObjectPageAnalyticalTable,
    ObjectPageGridTable,
    ObjectPageTreeTable,
    ObjectPageChart,
    ObjectPageForm
} from './index';
import type { ViewTypes, SAPUI5_FRAGMENT_CLASS, SAPUI5_VIEW_CLASS } from '../../common/webapp/manifest/sapUi5';

//Class implementations of the interfaces with sync rules at packages/specification/src/sync/v2/export/controls/ObjectPageSection.ts

/**
 * Section
 *
 * @isViewNode true
 */
export interface ObjectPageSectionV2 {
    /**
     * Defines if this section should be visible.
     */
    visible?: boolean;
}

/**
 * Form
 *
 * @isViewNode true
 */
export interface ObjectPageSectionFormV2 extends ObjectPageSectionV2 {
    form?: ObjectPageForm;
}

export type AnyObjectPageTableType =
    | ObjectPageResponsiveTableWithMultiSelect
    | ObjectPageResponsiveTableWithInlineDelete
    | ObjectPageAnalyticalTable
    | ObjectPageGridTable
    | ObjectPageTreeTable;

/**
 * Table
 *
 * @isViewNode true
 */
export interface ObjectPageSectionTableV2 extends ObjectPageSectionV2 {
    table?: AnyObjectPageTableType;
}

/**
 * Chart
 *
 * @isViewNode true
 */
export interface ObjectPageSectionChartV2 extends ObjectPageSectionV2 {
    chart?: ObjectPageChart;
}

export interface ObjectPageSectionContactV2 extends ObjectPageSectionV2 {
    /**
     * Contact
     *
     *  @isViewNode true
     */
    contact?: object;
}

export interface ObjectPageSectionAddressV2 extends ObjectPageSectionV2 {
    /**
     * Address
     *
     *  @isViewNode true
     */
    address?: object;
}

export interface ObjectPageSubSectionContactV2 extends ObjectPageSectionV2 {
    /**
     * Contact
     *
     *  @isViewNode true
     */
    contact?: object;
}

export interface ObjectPageSubSectionAddressV2 {
    /**
     * Address
     *
     *  @isViewNode true
     */
    address?: object;
}

export type AnySubSectionType =
    | ObjectPageSectionTableV2
    | ObjectPageSectionChartV2
    | ObjectPageSectionFormV2
    | ObjectPageSubSectionContactV2
    | ObjectPageSubSectionAddressV2;

export interface GenericSubSections {
    [key: string]: AnySubSectionType;
}

/**
 * Subsection
 *
 * @isViewNode true
 */
export interface ObjectPageSubSectionV2 extends ObjectPageSectionV2 {
    subsections?: GenericSubSections;
}

export type AnySectionType =
    | ObjectPageSectionTableV2
    | ObjectPageSectionChartV2
    | ObjectPageSectionFormV2
    | ObjectPageSectionContactV2
    | ObjectPageSectionAddressV2
    | ObjectPageSectionV2
    | ObjectPageSubSectionV2;

export interface GenericSections {
    [key: string]: AnySectionType;
}

export type CustomSectionTypeV2 = ObjectPageCustomSectionView | ObjectPageCustomSectionFragment;

/**
 * Custom Sections
 *
 * @isViewNode true
 */
export interface CustomSections {
    custom?: CustomSectionTypeV2[];
}

export interface ObjectPageCustomSectionBase {
    relatedFacet: string;
    relativePosition: SectionPosition;
    /**
     * The label of the custom section, preferably as an i18n key.
     *
     * @i18nClassification TIT: Custom section title
     */
    title: string;
}

/**
 * View
 *
 * @isViewNode true
 */
export interface ObjectPageCustomSectionView extends ObjectPageCustomSectionBase {
    className: typeof SAPUI5_VIEW_CLASS;
    viewName: string;
    type: ViewTypes;
}

/**
 * Fragment
 *
 * @isViewNode true
 */
export interface ObjectPageCustomSectionFragment extends ObjectPageCustomSectionBase {
    className: typeof SAPUI5_FRAGMENT_CLASS;
    fragmentName: string;
    type: ExtensionFragmentTypes;
}

export enum SectionPosition {
    AfterFacet = 'AfterFacet',
    BeforeFacet = 'BeforeFacet',
    ReplaceFacet = 'ReplaceFacet'
}

export enum ExtensionFragmentTypes {
    XML = 'XML'
}
